<template>
  <div v-if="loading" class="preloadFilter">
    <Loading />
  </div>
  <div v-else-if="isTeaserVisible" :class="$style.container">
    <figure :class="$style.figure">
      <figcaption :class="$style.caption">
        <div :class="$style.supLine">
          {{ storeTranslations.translations?.['shopCart.itemnumber'] || 'Article number' }}
          {{ shopArticle?.ItemId }} -
          {{ renderTypesLabel }}
          {{ renderTypes }}
        </div>
        <h3 v-if="article.title" :class="$style.title" v-html="article.title" />
        <div :class="$style.text" v-html="article.description" />
        <Button1XP
          :class="$style.linkDetail"
          size="xs"
          start-icon="docs"
          :text="
            storeTranslations.translations?.['productcode.standardarticle.detailLinkText'] ||
            'Details'
          "
          variant="link"
          @click="openModal"
        />
      </figcaption>
      <img
        v-if="article.image"
        :alt="article.title"
        :class="$style.image"
        loading="lazy"
        :src="article.image"
      />
    </figure>

    <div :class="$style.shop">
      <!-- PRICE -->
      <div :class="$style.shopPrice">
        {{ storeTranslations.translations?.['productcode.order.from'] || 'from' }}
        <IndividualPrice
          :btype-list="shopArticle.btypeList"
          :product-type="shopArticle.MainType"
          :quantity="Number(shopArticle.Quantity)"
          :value="Number(shopArticle.UnitPrice)"
        />
        <div>
          {{ storeTranslations.translations?.['productcode.order.excludingTax'] || 'plus Vat.' }}
          &amp;
          <a :class="$style.shippingLink" :href="shippingURL">{{
            storeTranslations.translations?.['productcode.shop.config.shippingText'] || 'Shipping'
          }}</a>
        </div>
      </div>

      <!-- SHOPCARTLINK -->
      <ShopCartLink
        v-if="isShop && shopArticle"
        :key="`cartLink-${shopArticle.ItemId}`"
        :add-button-loading-text="
          storeTranslations.translations?.['productcode.shop.config.addButtonLoadingText'] ||
          'Adding item'
        "
        :add-item-failed-msg="storeTranslations.translations?.['addItemFailed']"
        :item="{
          text: storeTranslations.translations?.['shopCart.buttonText'] || 'Add to cart',
          quantity: '1',
          id: shopArticle.ItemId,
          headline: article.title,
          subheadline: article.subHeadline,
          pictureurl: article.image,
          thumbnailurl: article.image,
          producturl: getShopUrl(),
        }"
        source="Search"
      />

      <Button1XP
        v-if="!isShop"
        :class="$style.goToShopLink"
        end-icon="arrow_forward"
        size="xs"
        :text="
          storeTranslations.translations?.['productcode.standardarticle.shopLinkText'] ||
          'Go to WIKA Shop'
        "
        :url="`${getShopUrl()}&itemId=${articleNumber}&highlightedText=${articleNumber}`"
        variant="secondary"
      />
    </div>

    <!-- MODAL -->
    <Modal :is-modal-open="isModalOpen" @close-modal="closeModal">
      <div :class="$style.modal">
        <p :class="$style.heading">
          {{ storeTranslations.translations?.['shopCart.itemnumber'] }} {{ articleNumber }}
        </p>
        <div class="columns is-gapless">
          <div class="column is-two-thirds-tablet">
            <p :class="$style.subHeading">
              {{
                storeTranslations.translations?.['productcode.standardarticle.detailLinkText'] ||
                'Details'
              }}
            </p>
            <p v-html="replaceNewLine(shopArticle.Longtext || shopArticle.Shorttext)" />
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script lang="ts">
import Loading from '../Loading/Loading.vue';
import ShopCartLink from '../ShopCartLink/ShopCartLink.vue';
import Modal from '../Modal/Modal.vue';
import { Category } from '../../globals/constants/search';
import { replaceNewLine } from '../../utils/helper';
import { computed, onMounted, ref, watch } from 'vue';
import IndividualPrice from '../IndividualPrice/IndividualPrice.vue';
import type { WindowWika } from '../../types/window';
import { useStoreTranslations } from '../../store/translations/translations';
import Button1XP from '../Button1XP/Button1XP.vue';
import safeFetch from '../../utils/safeFetch';
import type { SearchHit } from '../../types/search.ts';
import { useStoreConfiguration } from '../../store/configuration/configuration.ts';

type Props = {
  articleNumber: string;
  bgImgUrl: string;
  result: SearchHit;
  itemInfoUrl?: string;
};
</script>

<script setup lang="ts">
const props = defineProps<Props>();
// TODO: Stabilization Sprint remove this and add all the related values from configuration service
const _window = window as unknown as WindowWika;
const storeConfiguration = useStoreConfiguration();
const shippingURL = _window.shippingURL;
const isShop = _window.instance === 'shop';

const isModalOpen = ref(false);
const lastShopArticle = ref<string | undefined>(undefined);
const shopArticle = ref();
const loading = ref(false);

const article = computed(() => {
  const articles = (Array.isArray(props.result) ? props.result : [props.result]).filter((item) => {
    if (!item) {
      return;
    }

    return (
      (item._source?.categoryUid || item._source?.categoryuid) === Category.PRODUCTS ||
      ((item._source?.categoryUid || item._source?.categoryuid) === Category.SHOP &&
        item._source?.articles &&
        [...item._source.articles].indexOf(props.articleNumber) !== -1)
    );
  });

  if (articles.length > 0) {
    const { _id: id, _source } = articles[0];

    return {
      id,
      ..._source,
    };
  }

  return undefined;
});

const isTeaserVisible = computed(() => {
  return article.value && shopArticle.value && props.articleNumber === shopArticle.value.ItemId;
});

const openModal = () => {
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
};

const storeTranslations = useStoreTranslations();

const renderTypesLabel = computed(() => {
  const { MainType: type } = shopArticle.value ?? {};
  const productType = storeTranslations.translations?.['productType'];
  const productTypes = storeTranslations.translations?.['productTypes'];

  if (!type) {
    return '';
  }

  return type.trim().split(' ').length > 1 ? productTypes : productType;
});

const renderTypes = computed(() => {
  const { MainType: type } = shopArticle.value ?? {};

  if (!type) {
    return '';
  }

  return type
    .split(' ')
    .filter((value, index, self) => self.indexOf(value) === index)
    .join(', ');
});

const getShopUrl = () => {
  const { path, productType, producttype, standardArticle } = article.value;

  const baseUrl = isShop ? window.location.origin : _window.shopStartpageUrl;

  const type = productType || producttype;
  const shopUrl = type?.replaceAll('-', '_');

  const node =
    path && !path.hostname?.includes('shop')
      ? new URL(path, baseUrl).pathname.split('/').filter(Boolean).pop()
      : path;

  const url = new URL(node ? `${node}` : `${shopUrl}.WIKA`, baseUrl);

  if (standardArticle) {
    url.searchParams.set('tab', 'orderdata');
  }

  return url.toString();
};

const getShopArticle = () => {
  if (!storeConfiguration.config || !storeConfiguration.config.caas_getItemInfoUrl) {
    window.setTimeout(() => {
      getShopArticle();
    }, 100);

    return;
  }

  loading.value = true;

  if (!article.value || !props.articleNumber || props.articleNumber === lastShopArticle.value) {
    loading.value = false;

    return;
  }

  lastShopArticle.value = props.articleNumber;

  if (!props.itemInfoUrl) {
    return;
  }

  safeFetch(props.itemInfoUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      country: _window.currentCountry || storeConfiguration.caasConfig?.currentCountry,
      container: storeConfiguration.config?.pt_search_container,
      shopContainer: storeConfiguration.config?.pt_search_shop_container,
      itemid: props.articleNumber,
      language: (_window.currentLanguage || storeConfiguration.caasConfig?.language)?.replace(
        'EN-US',
        'EN',
      ),
    }),
  })
    .then((response) => {
      if (!response) {
        // TODO: check if notification is needed?
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => {
      if (data.Success) {
        shopArticle.value = data;
      }

      loading.value = false;
    })
    .catch(() => {
      shopArticle.value = undefined;
      loading.value = false;
    });
};

watch(article, (newArticle, oldArticle) => {
  if (!newArticle) {
    return;
  }
  if (!oldArticle || newArticle.id !== oldArticle.id) {
    getShopArticle();
  }
});

onMounted(() => {
  getShopArticle();
});
</script>

<style module lang="scss" src="./ResultTeaserArticle.scss" />
